/**
 * Composant InsertionTab pour la page Satisfaction
 * Affiche les statistiques d'insertion professionnelle par période
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from '../../components/ui/collapsible';
import { 
  TrendingUp, Users, Briefcase, Download, 
  ChevronDown, ChevronRight, Bell, Mail, CheckCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const getStatutEmploiLabel = (statut) => {
  const labels = {
    'en_emploi': 'En emploi',
    'en_recherche': 'En recherche',
    'autre_emploi_bpjeps': 'Autre emploi BPJEPS',
    'emploi_non_lie': 'Emploi non lié',
    'arret': 'Arrêt'
  };
  return labels[statut] || statut;
};

const getStatutEmploiColor = (statut) => {
  const colors = {
    'en_emploi': 'bg-green-100 text-green-700',
    'en_recherche': 'bg-blue-100 text-blue-700',
    'autre_emploi_bpjeps': 'bg-purple-100 text-purple-700',
    'emploi_non_lie': 'bg-amber-100 text-amber-700',
    'arret': 'bg-red-100 text-red-700'
  };
  return colors[statut] || 'bg-slate-100 text-slate-700';
};

const exportPeriodeXLS = (periode) => {
  let xlsContent = `INSERTION PROFESSIONNELLE - ${periode.periode.toUpperCase()}\n\n`;
  xlsContent += `Total moniteurs: ${periode.total}\n`;
  xlsContent += `Taux d'insertion: ${periode.taux_insertion}%\n\n`;
  xlsContent += `RÉPARTITION PAR STATUT\n`;
  xlsContent += `En emploi: ${periode.stats?.en_emploi || 0}\n`;
  xlsContent += `En recherche: ${periode.stats?.en_recherche || 0}\n`;
  xlsContent += `Autre emploi BPJEPS: ${periode.stats?.autre_emploi_bpjeps || 0}\n`;
  xlsContent += `Emploi non lié: ${periode.stats?.emploi_non_lie || 0}\n`;
  xlsContent += `Arrêt: ${periode.stats?.arret || 0}\n\n`;
  
  xlsContent += `LISTE DES MONITEURS\n`;
  xlsContent += 'Nom\tEmail\tStatut\tType emploi\tEmployeur\tAnnée diplôme\n';
  periode.moniteurs?.forEach(m => {
    xlsContent += `${m.nom}\t${m.email || ''}\t${getStatutEmploiLabel(m.statut)}\t${m.type_emploi || ''}\t${m.employeur || ''}\t${m.annee_diplome || ''}\n`;
  });
  
  const blob = new Blob([xlsContent], { type: 'application/vnd.ms-excel' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `insertion_${periode.periode.replace(/\s/g, '_')}.xls`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  toast.success(`Export ${periode.periode} téléchargé`);
};

export const InsertionTab = ({ stats, insertionParPeriode }) => {
  const [openPeriodes, setOpenPeriodes] = useState({});
  const [alertes, setAlertes] = useState([]);
  const [loadingAlertes, setLoadingAlertes] = useState(true);
  const [sendingRelance, setSendingRelance] = useState(null);

  useEffect(() => {
    const loadAlertes = async () => {
      try {
        const res = await axios.get('/moniteurs/alertes-relance');
        setAlertes(res.data);
      } catch (e) {
      }
      setLoadingAlertes(false);
    };
    loadAlertes();
  }, []);

  const handleRelance = async (userId, nom, echeance) => {
    const key = `${userId}-${echeance}`;
    setSendingRelance(key);
    try {
      await axios.post(`/moniteurs/relance/${userId}?echeance=${encodeURIComponent(echeance)}`);
      toast.success(`Email de relance (${echeance}) envoyé à ${nom}`);
      const res = await axios.get('/moniteurs/alertes-relance');
      setAlertes(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setSendingRelance(null);
  };

  const togglePeriode = (index) => {
    setOpenPeriodes(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div className="space-y-4" data-testid="insertion-tab">
      {/* Résumé global */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            Suivi de l'insertion professionnelle
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {[
              { key: 'en_emploi', label: 'En emploi', color: 'bg-green-500' },
              { key: 'en_recherche', label: 'En recherche', color: 'bg-blue-500' },
              { key: 'autre_emploi_bpjeps', label: 'Autre BPJEPS', color: 'bg-purple-500' },
              { key: 'emploi_non_lie', label: 'Non lié', color: 'bg-amber-500' },
              { key: 'arret', label: 'Arrêt', color: 'bg-red-500' },
            ].map(s => (
              <div key={s.key} className="text-center p-4 bg-slate-50 rounded-lg">
                <div className={`w-4 h-4 ${s.color} rounded-full mx-auto mb-2`} />
                <p className="text-2xl font-bold">{stats?.insertion?.by_statut?.[s.key] || 0}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Alertes de relance */}
      {!loadingAlertes && alertes.filter(a => !a.deja_relance).length > 0 && (
        <Card className="border-l-4 border-l-amber-400">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <Bell className="h-5 w-5" />
              Relances à effectuer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alertes.filter(a => !a.deja_relance).map((a, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-800">{a.nom}</p>
                    <p className="text-sm text-slate-500">{a.email} — {a.jours_depuis_diplome} jours depuis le diplôme</p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={sendingRelance === `${a.user_id}-${a.echeance}`}
                    onClick={() => handleRelance(a.user_id, a.nom, a.echeance)}
                    data-testid={`insertion-relance-${a.user_id}-${a.echeance.replace(/\s/g, '-')}`}
                  >
                    {sendingRelance === `${a.user_id}-${a.echeance}` ? (
                      <Clock className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Mail className="h-4 w-4 mr-1" />
                    )}
                    Relancer ({a.echeance})
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historique des relances envoyées */}
      {!loadingAlertes && alertes.filter(a => a.deja_relance).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-green-700 text-base">
              <CheckCircle className="h-5 w-5" />
              Relances déjà envoyées
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {alertes.filter(a => a.deja_relance).map((a, idx) => (
                <Badge key={idx} variant="outline" className="border-green-300 text-green-700 text-xs py-1 px-2">
                  {a.nom} — {a.echeance} ({new Date(a.date_relance).toLocaleDateString('fr-FR')})
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tableaux d'insertion par période - Collapsibles */}
      {insertionParPeriode.map((periode, index) => (
        <Collapsible 
          key={index} 
          open={openPeriodes[index]} 
          onOpenChange={() => togglePeriode(index)}
        >
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="bg-slate-50 border-b cursor-pointer hover:bg-slate-100 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    {openPeriodes[index] ? (
                      <ChevronDown className="h-5 w-5 text-slate-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-500" />
                    )}
                    <Briefcase className="h-5 w-5 text-blue-600" />
                    Insertion à {periode.periode}
                  </span>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-slate-200 text-slate-700">{periode.total} moniteurs</Badge>
                    <Badge className={`text-white ${periode.taux_insertion >= 70 ? 'bg-green-500' : periode.taux_insertion >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}>
                      {periode.taux_insertion}% d'insertion
                    </Badge>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        exportPeriodeXLS(periode);
                      }}
                      className="ml-2"
                      data-testid={`export-periode-${index}`}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      XLS
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="p-0">
                {/* Résumé par statut */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 p-4 bg-slate-50/50 border-b">
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">{periode.stats?.en_emploi || 0}</p>
                    <p className="text-xs text-slate-500">En emploi</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-blue-600">{periode.stats?.en_recherche || 0}</p>
                    <p className="text-xs text-slate-500">En recherche</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-purple-600">{periode.stats?.autre_emploi_bpjeps || 0}</p>
                    <p className="text-xs text-slate-500">Autre BPJEPS</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-amber-600">{periode.stats?.emploi_non_lie || 0}</p>
                    <p className="text-xs text-slate-500">Non lié</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-red-600">{periode.stats?.arret || 0}</p>
                    <p className="text-xs text-slate-500">Arrêt</p>
                  </div>
                </div>
                
                {/* Tableau des moniteurs */}
                {periode.moniteurs?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Nom</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Email</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Statut</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Type d'emploi</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Employeur</th>
                          <th className="text-left p-3 text-sm font-medium text-slate-600">Année diplôme</th>
                          <th className="text-center p-3 text-sm font-medium text-slate-600">Relance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {periode.moniteurs.map((m, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="p-3 font-medium">{m.nom}</td>
                            <td className="p-3 text-sm text-slate-600">{m.email || '-'}</td>
                            <td className="p-3">
                              <Badge className={getStatutEmploiColor(m.statut)}>
                                {getStatutEmploiLabel(m.statut)}
                              </Badge>
                            </td>
                            <td className="p-3 text-sm">{m.type_emploi || '-'}</td>
                            <td className="p-3 text-sm">{m.employeur || '-'}</td>
                            <td className="p-3 text-sm">{m.annee_diplome || '-'}</td>
                            <td className="p-3 text-center">
                              {(() => {
                                const mAlertes = alertes.filter(a => a.email === m.email && !a.deja_relance);
                                const userId = alertes.find(a => a.email === m.email)?.user_id;
                                if (mAlertes.length > 0 && userId) {
                                  return (
                                    <div className="flex gap-1 justify-center flex-wrap">
                                      {mAlertes.map(a => (
                                        <Button
                                          key={a.echeance}
                                          size="sm"
                                          className="text-xs h-6 px-2 bg-blue-600 hover:bg-blue-700 text-white"
                                          disabled={sendingRelance === `${userId}-${a.echeance}`}
                                          onClick={() => handleRelance(userId, a.nom, a.echeance)}
                                        >
                                          <Mail className="h-3 w-3 mr-1" />{a.echeance}
                                        </Button>
                                      ))}
                                    </div>
                                  );
                                }
                                return <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />;
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-6">Aucun moniteur dans cette période</p>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      ))}

      {insertionParPeriode.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">Aucune donnée d'insertion disponible</p>
            <p className="text-sm text-slate-400 mt-2">Les statistiques se construisent automatiquement à partir des profils moniteurs VNM</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default InsertionTab;
